import { Route, Routes, useLocation } from 'react-router';
import _ from 'lodash';
import axios from 'axios';

import 'material-symbols';
import 'material-symbols/outlined.css';

import '@fontsource/open-sans';
import '@fontsource/open-sans/300.css';
import '@fontsource/open-sans/400.css';
import '@fontsource/open-sans/500.css';
import '@fontsource/open-sans/600.css';
import '@fontsource/open-sans/700.css';
import '@fontsource/open-sans/800.css';
import '@fontsource/source-sans-pro/400.css';
import '@fontsource/source-sans-pro/600.css';
import '@fontsource/bitter/600.css';
import '@fontsource/bitter/400.css';

import './App.scss';

// get Content + config
import Content from './Content';

// Helper

// Pages
import Index from './pages/index/index';
import Login from './pages/login/index';

import { deactivateServices } from './_helper/deactivateServices';
import browserInfo from './_helper/browserInfo';
import { Revalidation } from './comp/revalidation/revalidation';
import ErrorBoundary from './_helper/ErrorBoundary';
import ScrollToTop from './_helper/ScrollToTop';
import LiveCredentialInfoLayer from './_helper/liveCredentialInfoLayer';
import FetchNewData from './_helper/fetchNewData';
import { ProtectedRoute } from './_helper/checkAccess';
import Logout from './pages/logout';
import SeatReservation from './pages/extra-services/seat-reservation';
import Excursion from './pages/extra-services/excursion';
import ExcursionDetails from './pages/extra-services/excursion/details';
import ExcursionRates from './pages/extra-services/excursion/rates';
import Insurance from './pages/extra-services/insurance';
import Transfer from './pages/extra-services/transfer';
import RailAndFly from './pages/extra-services/railAndFly';
import Cancellation from './pages/customerarea/cancellation';
import PartialCancellation from './pages/customerarea/cancellation/partial/index';
import PartialCancellationOverview from './pages/customerarea/cancellation/partial/overview';
import Booking from './pages/booking';
import BookingSuccess from './pages/booking-success';
import BookingFailed from './pages/booking/bookingFailed';
import Payment from './pages/customerarea/payment';
import PaymentRefund from './pages/customerarea/payment-refund';
import PaymentCredit from './pages/customerarea/payment-credit';
import Claim from './pages/customerarea/claim';
import DigitalTourGuide from './pages/customerarea/digital-tour-guide';
import EmergencyContact from './pages/customerarea/emergency-contact';
import Apis from './pages/customerarea/apis';
import NotFound from './pages/notFound';
import ConsentBanner from './comp/consentBanner';
import PrivacyPolicy from './pages/privacy-policy';
import { AppProvider } from './context/AppContext';

/* temporary user authorization for staging */
if (process.env.VITE_ENV === 'staging') {
    axios.defaults.headers.common['Authorization'] = 'Basic ' + btoa('service_team:7MevCzxhX9yTW9Hd');
}

// global vars
window.$deactivateLogin = Boolean(process.env.VITE_DEACTIVATE_LOGIN === 'true');
window.$affiliate = process.env.VITE_AFFILIATE ?? false;
window.$affiliateServices = process.env.VITE_AFFILIATE_ACTIVATE_SERVICES;

window.$baseUrl = process.env.VITE_BASE_URL;
window.$website = process.env.VITE_WEBSITE;
window.$requestUrl = process.env.VITE_REQUEST_URL;
window.$serviceUrl = process.env.VITE_SERVICE_URL;
window.$tourGuideRegistrationUrl = process.env.VITE_TOUR_GUIDE_REGISTRATION_URL;
window.$token = 'z546bc032cfb5a6fa585aa8031dreact';
window.$testLoginAllowed = process.env.VITE_TEST_LOGIN_ALLOWED;
window.$3DS2Allowed = process.env.VITE_3DS2_ALLOWED;
window.$revalidateEnabled = Boolean(process.env.VITE_REVALIDATE_ENABLED === 'true');
window.$allowFlightChangeSearch = process.env.VITE_ALLOW_FLIGHT_CHANGE_SEARCH;
window.$allowHotelChangeSearch = process.env.VITE_ALLOW_HOTEL_CHANGE_SEARCH;
window.$allowEmergencyContact = process.env.VITE_ALLOW_EMERGENCY_CONTACT;
window.$bookingCancelled = false;
window.$searchFrontendUrl = process.env.VITE_SEARCH_FRONTEND_URL;
window.$claimEnabled = process.env.VITE_CLAIM_ENABLED;
window.$claimUploadSize = process.env.VITE_CLAIM_UPLOAD_SIZE;
window.$requestData = {};
window.$browserInfo = JSON.parse(browserInfo());
window.$bookingState = '';

window.$cookieName = 'CookieConsentCustomerArea';
window.$content = Content;

// deactivate extras
window.$deactivateExtras = [];
if (process.env.VITE_DEACTIVATE_EXTRAS !== undefined) {
    window.$deactivateExtras = JSON.parse(process.env.VITE_DEACTIVATE_EXTRAS);

    let deactivateExtras = window.$deactivateExtras;
    _.forEach(deactivateExtras, function (v: string) {
        if (window.$content.extras[v]?.active !== undefined) {
            window.$content.extras[v].active = false;
        }
    });
}

// deactivate services
window.deactivateServices = [];
deactivateServices();

if (process.env.VITE_ENV === 'production') {
    delete window.$content.fakeLogin;
}

window.$documentTitle = ' | ' + process.env.VITE_SEO_TITLE;

window.$autoLogin = false;

// set devicetype : mobile | desktop
if (/Android|webOS|iPhone|iPad|iPod|BlackBerry/i.test(navigator.userAgent)) {
    sessionStorage.setItem('devicetype', 'mobile');
} else {
    sessionStorage.setItem('devicetype', 'desktop');
}

// affiliate
if (window.$affiliate) {
    switch (window.$affiliate) {
        case 'ch24':
            window.$documentTitle = ' | CHECK24 Kundenbereich bei AurumTours';
            break;
        default:
            window.$documentTitle = ' | Aurumtours';
    }

    document.querySelector('html')?.classList.add('affiliate-' + window.$affiliate);
}

const App = () => {
    const location = useLocation();
    
    return (
        <ErrorBoundary location={location}>
            <ScrollToTop />
            <LiveCredentialInfoLayer />

            {!window.$affiliate && <ConsentBanner />}

            <Routes>
                <Route path="/deeplink/:target/:hash?/:mode?" element={<Login location={location} />} />

                <Route path="/login" element={<Login location={location} />} />
                <Route path="/logout" element={<Logout />} />

                {!window.$affiliate && (
                    <Route
                        path="/datenschutz"
                        element={
                            <AppProvider>
                                <PrivacyPolicy />
                            </AppProvider>
                        }
                    />
                )}

                <Route
                    path="/revalidierung"
                    element={
                        <ProtectedRoute>
                            <Revalidation />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/sitzplatzreservierung"
                    element={
                        <ProtectedRoute>
                            <SeatReservation />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/ausfluege"
                    element={
                        <ProtectedRoute>
                            <Excursion />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/ausfluege/details/:id"
                    element={
                        <ProtectedRoute>
                            <ExcursionDetails />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/ausfluege/rates/:id"
                    element={
                        <ProtectedRoute>
                            <ExcursionRates />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/versicherung"
                    element={
                        <ProtectedRoute>
                            <Insurance />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/transfer"
                    element={
                        <ProtectedRoute>
                            <Transfer />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/zug-zum-flug"
                    element={
                        <ProtectedRoute>
                            <RailAndFly />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/stornierung"
                    element={
                        <ProtectedRoute>
                            <Cancellation />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/leistung-stornieren/:service?"
                    element={
                        <ProtectedRoute>
                            <PartialCancellation />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/leistung-stornieren/:service/uebersicht/:index"
                    element={
                        <ProtectedRoute>
                            <PartialCancellationOverview />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/buchung/:travelType?"
                    element={
                        <ProtectedRoute>
                            <Booking />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/uebersicht"
                    element={
                        <ProtectedRoute>
                            <BookingSuccess />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/buchung-fehlgeschlagen"
                    element={
                        <ProtectedRoute>
                            <BookingFailed />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/zahlung"
                    element={
                        <ProtectedRoute>
                            <Payment />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/rueckzahlung"
                    element={
                        <ProtectedRoute>
                            <PaymentRefund />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/gutschrift"
                    element={
                        <ProtectedRoute>
                            <PaymentCredit />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/reklamation"
                    element={
                        <ProtectedRoute>
                            <Claim />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/digitale-reiseleitung"
                    element={
                        <ProtectedRoute>
                            <DigitalTourGuide />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/notfall-kontakt"
                    element={
                        <ProtectedRoute>
                            <EmergencyContact />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/apis"
                    element={
                        <ProtectedRoute>
                            <Apis />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/:index?"
                    element={
                        <ProtectedRoute>
                            <Index location={location} />
                        </ProtectedRoute>
                    }
                />

                <Route path="*" element={<ProtectedRoute>{<NotFound />}</ProtectedRoute>} />
            </Routes>

            <FetchNewData location={location} />
        </ErrorBoundary>
    );
};
export default App;
